/**
 * #5524 CTO fold-round (2026-08-30, item 3): extracted from metric-registry.ts's
 * FINANCE_SEMANTIC_LAYER_REGISTRY_SEED to keep that file under the size law. Pure data
 * relocation — no logic change, no row content touched beyond the file move itself. Same
 * shape as the sibling *.data.ts files (metric-registry.sga-spend-existing.data.ts,
 * metric-registry.ma-deal.data.ts, metric-registry.finance-lever-contributions-a.data.ts): a
 * typed array of raw seed-input objects the main file maps through metricEntry() and splices
 * back into FINANCE_SEMANTIC_LAYER_REGISTRY_SEED at the exact position it came from, so the
 * compiled registry, its row order, and every exported symbol are unchanged.
 *
 * Contains six 3PL industry-profile supplemental metric rows: two non-segment supplemental
 * lines (adjusted_income_from_operations_usd, d_and_a_usd, avg_employees) and the three
 * reportable-segment income-from-operations rows (nast/global_forwarding/all_other_corporate
 * .income_from_operations_usd — see measure-identity-segment-oi-three-way-parity.spec.ts for
 * the parity invariant across them). All six measureIdentity values are content-identical to
 * origin/main at fold time (b8b7a72ecd) — the bulk measure-identity-declaration wave declared
 * all six independently and in parallel with this branch; this extraction relocates main's
 * post-declaration content, not a re-declaration of this branch's own pre-fold values.
 */
import { OPERATING_INCOME_MEASURE_IDENTITY } from './governed-measure-identity';
import type { MetricEntrySeedInput } from './metric-registry';

export const FINANCE_3PL_SUPPLEMENTAL_METRIC_REGISTRY_SEED_INPUTS: readonly MetricEntrySeedInput[] =
  Object.freeze([
    {
      metricKey: 'adjusted_income_from_operations_usd',
      measureIdentity: OPERATING_INCOME_MEASURE_IDENTITY,
      label: 'Adjusted income from operations',
      definition: '3PL adjusted operating income from approved company finance evidence.',
      reconciliationRule: 'Maps to canonical adjusted operating income for adjusted operating margin.',
      source:
        'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_INDUSTRY_PROFILES/3pl-logistics/adjusted_income_from_operations_usd',
      ownerModuleId: 'finance',
      sourceBindingKeys: ['adjusted_income_from_operations_usd'],
      registryLayer: 'industry',
      industryProfileId: '3pl-logistics',
    },
    {
      metricKey: 'd_and_a_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      direction: 'context',
      label: 'D&A',
      definition: 'Depreciation and amortization from approved company finance evidence.',
      reconciliationRule: 'Maps to canonical D&A.',
      source:
        'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_INDUSTRY_PROFILES/3pl-logistics/d_and_a_usd',
      ownerModuleId: 'finance',
      sourceBindingKeys: ['d_and_a_usd'],
      registryLayer: 'industry',
      industryProfileId: '3pl-logistics',
    },
    {
      metricKey: 'avg_employees',
      measureIdentity: { measuredDimension: 'other', valueForm: 'count', unitFamily: 'count', normalizationBasis: 'none' },
      label: 'Average employees',
      definition: 'Average employee count from approved company finance evidence.',
      reconciliationRule: 'Maps to canonical reporting headcount.',
      source:
        'apps/api/src/decision-modules/finance/finance.profile.ts#metricCall:FINANCE_INDUSTRY_PROFILES/3pl-logistics/avg_employees',
      ownerModuleId: 'finance',
      sourceBindingKeys: ['avg_employees'],
      registryLayer: 'industry',
      industryProfileId: '3pl-logistics',
      unit: 'count',
      temporalType: 'count',
    },
    {
      metricKey: 'nast.income_from_operations_usd',
      measureIdentity: OPERATING_INCOME_MEASURE_IDENTITY,
      label: 'NAST income from operations',
      definition: 'CHRW NAST segment income from operations from approved segment evidence.',
      reconciliationRule: 'Segment line from CHRW reportable segment evidence.',
      source:
        'apps/api/src/decision-modules/finance/finance.profile.ts#supplementalLineKey:FINANCE_INDUSTRY_PROFILES/3pl-logistics/nast_income_from_operations_usd',
      ownerModuleId: 'finance',
      sourceBindingKeys: ['nast_income_from_operations_usd'],
      registryLayer: 'industry',
      industryProfileId: '3pl-logistics',
      appliesTo: [{ dimension: 'business_line', members: ['nast'] }],
    },
    {
      metricKey: 'global_forwarding.income_from_operations_usd',
      measureIdentity: OPERATING_INCOME_MEASURE_IDENTITY,
      label: 'Global Forwarding income from operations',
      definition:
        'CHRW Global Forwarding segment income from operations from approved segment evidence.',
      reconciliationRule: 'Segment line from CHRW reportable segment evidence.',
      source:
        'apps/api/src/decision-modules/finance/finance.profile.ts#supplementalLineKey:FINANCE_INDUSTRY_PROFILES/3pl-logistics/global_forwarding_income_from_operations_usd',
      ownerModuleId: 'finance',
      sourceBindingKeys: ['global_forwarding_income_from_operations_usd'],
      registryLayer: 'industry',
      industryProfileId: '3pl-logistics',
      appliesTo: [{ dimension: 'business_line', members: ['global_forwarding'] }],
    },
    {
      metricKey: 'all_other_corporate.income_from_operations_usd',
      measureIdentity: OPERATING_INCOME_MEASURE_IDENTITY,
      label: 'All Other and Corporate income from operations',
      definition:
        'CHRW All Other and Corporate segment income from operations from approved segment evidence.',
      reconciliationRule: 'Segment line from CHRW reportable segment evidence.',
      source:
        'apps/api/src/decision-modules/finance/finance.profile.ts#supplementalLineKey:FINANCE_INDUSTRY_PROFILES/3pl-logistics/all_other_corporate_income_from_operations_usd',
      ownerModuleId: 'finance',
      sourceBindingKeys: ['all_other_corporate_income_from_operations_usd'],
      registryLayer: 'industry',
      industryProfileId: '3pl-logistics',
      appliesTo: [{ dimension: 'business_line', members: ['all_other_corporate'] }],
    },
  ]);
